<script lang="ts" setup>
import type { PropType} from 'vue';
import { computed, onMounted, ref } from 'vue';
import type { ICellRendererParams } from 'ag-grid-community';
import { VeProgress } from 'vue-ellipse-progress';
import { SysarbColors } from '@/types';

type ExtendedParams = ICellRendererParams & {
    total: (params: ICellRendererParams) => number;
    currentValue: (params: ICellRendererParams) => number;
};

const props = defineProps({
    params: {
        type: Object as PropType<ExtendedParams>,
        required: true
    }
});

const total = ref<number | undefined>(0);
const currentValue = ref<number | undefined>(0);
const percentage = ref<number | undefined>(0);

const getColor = computed(() => {
    if (percentage.value === 100) {
        return SysarbColors.GREEN_500;
    }
    if (percentage.value === 0) {
        return SysarbColors.GRAY_200;
    }
    return SysarbColors.BLUE_600;
});

const getFillColor = computed(() => {
    if (percentage.value === 100) {
        return SysarbColors.GREEN_500;
    }
    return undefined;
});

const emptyColor = SysarbColors.GRAY_200;

onMounted(() => {
    total.value = typeof props.params.total === 'function' ? props.params.total(props.params) : props.params.total;
    currentValue.value = typeof props.params.currentValue === 'function' ? props.params.currentValue(props.params) : props.params.currentValue;
    percentage.value = total.value === 0 ? 100 : Math.floor((currentValue.value / total.value) * 100);
});
</script>

<template>
    <div class="flex-container align-middle height-100 sy-spacer-x-5">
        <div class="sy-circular-progress-container flex-container align-middle">
            <VeProgress
                :progress="percentage"
                :size="24"
                :color="getColor"
                :color-fill="getFillColor"
                thickness="10%"
                empty-thickness="10%"
                :empty-color="emptyColor"
                :hide-legend="true"
                :animation="percentage === 100 ? 'default 0' : 'default 400'"
            />
            <icon v-show="percentage === 100" class="sy-circular-icon sy-icon-transition" icon="check"></icon>
        </div>
        <span>{{ percentage }} %</span>
    </div>
</template>

<style lang="scss">
.sy-circular-progress-container {
    position: relative;
    .sy-circular-icon {
        position: absolute;
        color: var(--sy-white);
        stroke-width: 4px;
        width: 12px;
        height: 12px;
        top: 6px;
        left: 6px;
        animation-name: bounce;
        animation-duration: 1000ms;
        animation-timing-function: ease;
        @keyframes bounce {
            0%   { transform: translateY(2px); }
            50%  { transform: translateY(-1px); }
            100% { transform: translateY(0); }
        }
    }
}
</style>
